import React, { useEffect, useMemo, useRef } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { usePermissions } from "@features/entity/hooks/usePermissions";

type RequireMode = "route" | "section" | "disable";

interface RequirePermissionProps {
    /** One action code or several. */
    action: string | string[];
    /** Scope to check the action against (NATION | DISTRICT | CHURCH). */
    scopeType?: string;
    /** Entity id within the scope. */
    scopeId?: number;
    /** How to behave when the user lacks permission. Default: "section". */
    mode?: RequireMode;
    /** When multiple actions are given, require all of them (default: any). */
    requireAll?: boolean;
    /** What to render when denied (route/section modes). */
    fallback?: React.ReactNode;
    /** What to render while the permission check is in flight (route/section). */
    loadingFallback?: React.ReactNode;
    children?: React.ReactNode;
}

/**
 * Gates its children on the current user's permission for an action at a given scope,
 * using the existing `usePermissions` hook (POST /user/access). This is a
 * defense-in-depth / UX layer only — the API still enforces every action server-side.
 *
 * - mode="section" (default): render nothing (or `fallback`) when denied — use to hide a control/panel.
 * - mode="disable": always render children, but disabled when denied — use for action buttons.
 * - mode="route": render `fallback` (or a "Not authorized" notice) when denied — use for whole pages.
 */
const RequirePermission: React.FC<RequirePermissionProps> = ({
    action,
    scopeType,
    scopeId,
    mode = "section",
    requireAll = false,
    fallback = null,
    loadingFallback = null,
    children,
}) => {
    const actionCodes = useMemo(
        () => (Array.isArray(action) ? action : [action]),
        [action]
    );

    const { hasAllPermissions, hasAnyPermission, loading } = usePermissions({
        scopeType,
        scopeId,
        actionCodes,
    });

    // Track whether at least one permission check has started and finished, so we can
    // show a pending state instead of flashing "denied" before the first check runs.
    const startedRef = useRef(false);
    useEffect(() => {
        if (loading) startedRef.current = true;
    }, [loading]);

    const hasScope = !!scopeType && !!scopeId;
    const settled = startedRef.current && !loading;
    const pending = hasScope && !settled;

    const allowed = hasScope && (requireAll ? hasAllPermissions(actionCodes) : hasAnyPermission(actionCodes));

    if (pending) {
        if (mode === "disable") {
            return (
                <fieldset disabled className="border-0 p-0 m-0" style={{ minInlineSize: 0 }}>
                    {children}
                </fieldset>
            );
        }
        return (
            <>
                {loadingFallback ?? (
                    mode === "route" ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                        </div>
                    ) : null
                )}
            </>
        );
    }

    if (allowed) {
        return <>{children}</>;
    }

    // Denied
    if (mode === "disable") {
        return (
            <fieldset disabled className="border-0 p-0 m-0" style={{ minInlineSize: 0 }}>
                {children}
            </fieldset>
        );
    }
    if (mode === "route") {
        return (
            <>
                {fallback ?? (
                    <Alert variant="warning" className="my-4">
                        You don't have permission to view this page.
                    </Alert>
                )}
            </>
        );
    }
    // section
    return <>{fallback}</>;
};

export default RequirePermission;
